import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import Measure, { type ContentRect } from "react-measure";
import { Document, Page, pdfjs } from "react-pdf";
import { useDebouncedCallback } from "use-debounce";
import { LoadingSpinner, VStack } from "widgets";

import style from "@/modules/files/styles/FilePreviewDialog.module.scss";

export interface PdfPreviewActionsApi {
  zoomIn: () => void;
  zoomOut: () => void;
}

interface PDFPreviewProps {
  pdfUrl: string;
}

export const PDFPreview = forwardRef<PdfPreviewActionsApi, PDFPreviewProps>(function PDFPreview(props, ref) {
  const [numPages, setNumPages] = useState<number>(0);

  const zoomFactor = 1.2;

  const [viewWidth, setViewWidth] = useState<number | undefined>(0);
  const [scale, setScale] = useState<number>(0.8);
  const [allPageNumbers, setAllPageNumbers] = useState<number[]>([]);

  useEffect(() => {
    // FIXME 2025-10-20, Christian Ribeaud: remove this polyfill in the next update when the
    //  minimally supported browser versions are raised.
    if (!URL.parse && window) {
      window.URL.parse = function (url, base) {
        try {
          return new URL(url, base);
        } catch {
          return null;
        }
      };
    }
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString();
  }, []); // Run only once after component mounts

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);

      for (let p = 1; p < numPages + 1; p++) {
        allPageNumbers.push(p);
      }
      setAllPageNumbers(allPageNumbers);
    },
    [allPageNumbers],
  );

  const zoomOut = () => {
    setScale(Math.max(scale / zoomFactor, 0.2));
  };

  const zoomIn = () => {
    setScale(Math.min(scale * zoomFactor, 5));
  };

  useImperativeHandle(ref, () => {
    return { zoomIn: zoomIn, zoomOut: zoomOut };
  });

  const debouncedViewWidthChanged = useDebouncedCallback((contentRect: ContentRect) => {
    setViewWidth(contentRect.bounds?.width);
  }, 100);

  return (
    <VStack style={{ width: "100%" }}>
      <Measure bounds onResize={debouncedViewWidthChanged}>
        {({ measureRef }) => (
          <div ref={measureRef} style={{ overflow: "auto", padding: 20, backgroundColor: "#d9e4ed66" }}>
            <Document file={props.pdfUrl} loading={<LoadingSpinner inCenter />} onLoadSuccess={onDocumentLoadSuccess}>
              {allPageNumbers.length > 0
                ? allPageNumbers.map((pn) => (
                    <>
                      <Page
                        key={`pdf-page-${pn}`}
                        pageNumber={pn}
                        width={(viewWidth! - 60) * scale}
                        renderMode="canvas"
                        loading={<LoadingSpinner inCenter />}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className={style.pdfPreviewPage}
                      />
                      {pn !== numPages && <div style={{ height: 20 }} />}
                    </>
                  ))
                : undefined}
            </Document>
          </div>
        )}
      </Measure>
    </VStack>
  );
});
